import { useEffect, useState } from "react";
import { Box, TextareaAutosize, Tooltip, Typography } from "@mui/material";

interface InlineNoteCellProps {
  /** Current note text. */
  note?: string;
  /** When false, renders read-only (no inline editing). */
  canEdit: boolean;
  /** Persists the new value. Return true on success, false to revert. */
  onSave: (value: string) => Promise<boolean>;
  /** Placeholder shown when empty and editable (compact variant). */
  placeholder?: string;
  /** block = full-width multiline section (detail pages); default = compact ellipsis table cell. */
  block?: boolean;
}

const compactSx = {
  maxWidth: 220,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};

/**
 * Shared inline view/edit note control (US 5273 / ADR-052 §10). Click to edit,
 * save on blur via `onSave`; Escape cancels. Stops click propagation so it can
 * live inside expandable table rows. Used for the claimant policy note (Summary
 * table + claimant page) and the stop-loss claim note (Claims Detail), each
 * wiring its own `onSave`.
 */
export const InlineNoteCell = ({
  note,
  canEdit,
  onSave,
  placeholder = "Add note…",
  block = false,
}: InlineNoteCellProps) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note ?? "");
  const [saving, setSaving] = useState(false);

  // Re-sync when the underlying note changes (e.g. after a refresh) — but never
  // while editing, so an external refresh() (e.g. a sibling note save) can't
  // overwrite the user's in-progress text. On exiting edit mode this re-syncs.
  useEffect(() => {
    if (!editing) setValue(note ?? "");
  }, [note, editing]);

  if (!canEdit) {
    if (block) {
      return (
        <Typography variant="body2" color={note ? "text.primary" : "text.disabled"} sx={{ whiteSpace: "pre-wrap" }}>
          {note || "—"}
        </Typography>
      );
    }
    return (
      <Tooltip title={note || ""} placement="top-start" disableHoverListener={!note} arrow>
        <Typography variant="body2" sx={{ ...compactSx, color: "text.secondary" }}>
          {note || "—"}
        </Typography>
      </Tooltip>
    );
  }

  const commit = async () => {
    if (saving) return;
    const trimmed = value.trim();
    if (trimmed === (note ?? "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onSave(trimmed);
    setSaving(false);
    if (ok) {
      setEditing(false);
    } else {
      setValue(note ?? "");
    }
  };

  const cancel = () => {
    setValue(note ?? "");
    setEditing(false);
  };

  if (editing) {
    return (
      // Wrapper stops row-expand toggles when editing inside a table row. On
      // blur out of the cell, auto-close ONLY when there are no unsaved edits —
      // this clears stray open editors (e.g. across table rows) without ever
      // discarding in-progress text; unsaved edits keep the editor open so the
      // user must Save/Cancel explicitly.
      <Box
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
          if (value.trim() === (note ?? "")) setEditing(false);
        }}
      >
        <TextareaAutosize
          autoFocus
          minRows={block ? 3 : 1}
          value={value}
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            // Plain Enter = newline (multi-line notes). Cmd/Ctrl+Enter saves; Esc cancels.
            if (e.key === "Escape") {
              cancel();
            } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void commit();
            }
          }}
          style={{ width: "100%", font: "inherit", resize: "vertical" }}
        />
        <Box sx={{ display: "flex", gap: 1.5, mt: 0.5, alignItems: "center" }}>
          <Typography variant="caption" color="text.disabled" sx={{ mr: "auto" }}>
            {saving ? "Saving…" : block ? "⌘/Ctrl+Enter to save · Esc to cancel" : ""}
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            sx={{
              cursor: saving ? "default" : "pointer",
              fontWeight: 600,
              "&:hover": { textDecoration: saving ? "none" : "underline" },
            }}
            onClick={() => void commit()}
          >
            Save
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            onClick={cancel}
          >
            Cancel
          </Typography>
        </Box>
      </Box>
    );
  }

  if (block) {
    return (
      <Box
        onClick={() => setEditing(true)}
        sx={{
          cursor: "pointer",
          minHeight: 60,
          p: 1,
          borderRadius: 1,
          border: "1px dashed",
          borderColor: "divider",
          "&:hover": { bgcolor: "action.hover", borderColor: "text.secondary" },
        }}
      >
        <Typography variant="body2" color={note ? "text.primary" : "text.disabled"} sx={{ whiteSpace: "pre-wrap" }}>
          {note || "Click to add notes..."}
        </Typography>
      </Box>
    );
  }

  return (
    <Tooltip title={note || "Click to add note"} placement="top-start" arrow>
      <Typography
        variant="body2"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        sx={{
          ...compactSx,
          cursor: "pointer",
          color: note ? "text.secondary" : "text.disabled",
          borderRadius: 0.5,
          px: 0.5,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        {note || placeholder}
      </Typography>
    </Tooltip>
  );
};
